<template lang="html">
  <div>
    <portal to="app-right-panel-title">
      {{ $t('general.filter.title') }}
    </portal>
    <portal to="app-right-panel" v-if="filterFields.length" :disabled="disabled">
      <form @submit.prevent="submitFilters" class="pa-4">
        <div class="pt-2" v-for="(field, index) in filterFields" :key="index">
          <v-skeleton-loader
            v-if="!filterPanelIsFullyOpen"
            :key="`loader-${index}`"
            type="list-item-two-line"
            class="mb-n5"
          ></v-skeleton-loader>
          <IndexFilterField
            v-show="filterPanelIsFullyOpen"
            ref="filter"
            v-model="$v.filterValues[field.name].$model"
            v-bind="field"
            :autofocus="$vuetify.breakpoint.smAndUp && filterPanelIsFullyOpen && index == 0"
            :validation-status="$v.filterValues[field.name]"
            @keyup.enter="onPageChange($event); submitFilters();"
            @change:search="addSearch"
          />
        </div>
      </form>
    </portal>
    <portal to="app-right-panel-append" v-if="filterFields.length">
      <div class="formbuttons">
        <v-btn
          @click="resetFilters"
          outlined
          color="primary"
        >
          {{ $t('general.clear') }}
        </v-btn>
        <v-btn
          @click="submitFilters"
          color="primary"
          :disabled="$v.filterValues.$invalid"
          type="submit"
        >
          {{ $t('general.apply') }}
        </v-btn>
      </div>
    </portal>
  </div>
</template>

<script>
import { get, sync } from 'vuex-pathify';
import { upperFirst } from 'lodash';
import IndexFilterField from './IndexFilterField.vue';

export default {
  model: {
    // :filters.sync to automatically sync
    // :filters & @submit to wait for submit
    prop: 'filters',
    event: 'update:filters',
  },
  components: {
    IndexFilterField,
  },
  props: {
    filterFields: {
      // config of filters that have an input field.
      // must be Object containing IndexFilterField props
      type: Array,
      required: true,
    },
    filters: {
      // active filters object, Object with {filter_name: value}
      type: Object,
      required: true,
    },
    resetValue: {
      // when clearing filters, reset to this value (when certain filters are mandatory)
      type: Object,
      default: () => ({}),
    },
    disabled: {
      // HACK: disable portal to right panel to close content.
      // constant adding/removing causes perf issue,
      // also doesn't sync state when closed from toolbar
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      filterValues: {},
    };
  },
  computed: {
    filterPanelOpen: sync('rightPanel/isOpen'),
    filterPanelIsFullyOpen: get('rightPanel/isFullyOpen') || false,
    formattedData() {
      return Object.fromEntries(
        Object.entries(this.filterValues)
          .filter(([_, v]) => !!v || v === false || v === 0)
          .map(([k, v]) => [k, this.stringifyValue(v)])
      );
    },
  },
  watch: {
    filters: {
      immediate: true,
      deep: true,
      handler: 'init',
      // REFACTOR: Handler currently replaces entire state, to set key that is removed to null.
      // could instead check the new/old object to check for key omission.
      // Take care when mutating object.
    },
    filterValues: {
      deep: true,
      handler() {
        // TODO: add debounce
        if (this.$v.filterValues.$invalid) return;
        this.$emit('update:filters', this.formattedData);
      },
    },
  },
  methods: {
    upperFirst,
    isOverlayApplied() {
      return document.querySelector('.v-overlay--active');
    },
    handleEnterPress(event) {
      //if search input or date picker (or other) dialog is enabled,
      //we dont want when pressing Enter to submit value and submit filters at the same time
      if (event.key === 'Enter' && !this.isOverlayApplied()) {
        this.submitFilters();
      }
    },
    init() {
      this.filterFields.forEach(field =>
        this.$set(this.filterValues, field.name, null)
      );

      Object.entries(this.filters).forEach(([k, v]) => {
        if (v == 'false' || v == 'true') {
          this.filterValues[k] = JSON.parse(v);
        } else {
          this.filterValues[k] = v;
        }
      });
    },

    addSearch(search) {
      this.$refs.filter.forEach(filter =>
        filter.updateParams(search?.scope, search?.id)
      );
    },
    /*
      fixes bug of shifting layout when applying a textfield filter, nav drawer closed, 
      and then pressing a keyboard key which shifts the layout
    */
    onPageChange(event){
      // call blur() to remove focus from filter when navigation drawer is closed 
      event.target.blur(); 
    },
    submitFilters() {
      this.$emit('submit', this.formattedData);
      this.filterPanelOpen = false;
    },
    resetFilters() {
      this.$emit('reset:filters', {});
    },
    stringifyValue(value) {
      if (value instanceof Date) return value.toLocalISOString();
      return value;
    },
  },
  validations() {
    let schema = { filterValues: {} };

    this.filterFields.forEach(field => {
      schema.filterValues[field.name] = field.validations || {};
    });

    return schema;
  },
};
</script>

<style lang="scss" scoped>
.formbuttons {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: $spacer * 4;
  max-width: 100%;

  > * {
    flex: 1 1 100%;
  }

  > * + * {
    margin-left: $spacer * 4;
  }
}
</style>
